import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    Box,
    Flex,
    Image,
    useMediaQuery,
    useMultiStyleConfig,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {motion, useMotionValueEvent, useScroll, useTransform} from 'framer-motion'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'

const DataSlide = ({
    slideNr,
    slideData,
    isVideoPlayed,
    videoRef,
    onVideoEndHandler,
    styles,
    visibleSlide,
    slidesTransition,
    mobileMediaQuery
}) => {
    return (
        <motion.div
            initial={{opacity: visibleSlide === slideNr ? 0 : 1}}
            animate={{opacity: visibleSlide === slideNr ? 1 : 0}}
            transition={slidesTransition}
            style={styles.slideContainer}
        >
            <picture>
                <source media={mobileMediaQuery} srcSet={slideData.imageMobile} />
                <Image
                    src={slideData.image}
                    style={{
                        display: isVideoPlayed ? 'block' : 'none'
                    }}
                    {...styles.staticImage}
                    playsInline={true}
                />
            </picture>
            <motion.video
                ref={videoRef}
                autoPlay={false}
                loop={false}
                controls={false}
                muted
                playsInline={true}
                onEnded={onVideoEndHandler}
                initial={{opacity: isVideoPlayed ? 0 : 1}}
                animate={{opacity: isVideoPlayed ? 0 : 1}}
                style={styles.videoPlayer}
                className={'dataSlidesVideo'}
            >
                <source src={slideData.videoMobile} type="video/mp4" media={mobileMediaQuery} />
                <source src={slideData.video} type="video/mp4" />
                Your browser does not support the video tag.
            </motion.video>
            {slideData.richText && (
                <Flex
                    className="overlayContainer"
                    {...styles.overlayContainer}
                    justifyContent={slideData.overlayAlignmentX === 'right' ? 'end' : 'auto'}
                >
                    <Box m="0" {...styles.overlayInnerContainer}>
                        <Image
                            src={getAssetUrl(`static/dataSlides/step${slideNr}.png`)}
                            alt=""
                            width={{base: '60px', xl: '91px'}}
                            height={{base: '60px', xl: '91px'}}
                            mr={{xl: 8}}
                            mb={{base: 8, xl: 0}}
                        />
                        <RTE
                            richText={slideData.richText}
                            textStyles={{base: 'light3Xlarge', xl: 'light6Xlarge'}}
                            sx={{
                                p: {
                                    fontSize: {base: 'lg', xl: '28px'},
                                    lineHeight: {base: 7, xl: 11}
                                }
                            }}
                        />
                    </Box>
                </Flex>
            )}
        </motion.div>
    )
}
DataSlide.propTypes = {
    slideNr: PropTypes.number,
    slideData: PropTypes.object,
    isVideoPlayed: PropTypes.bool,
    videoRef: PropTypes.object,
    onVideoEndHandler: PropTypes.func,
    styles: PropTypes.object,
    visibleSlide: PropTypes.number,
    slidesTransition: PropTypes.object,
    mobileMediaQuery: PropTypes.string
}

/**
 * LandingPageDataSlider component
 *
 * @param {object} props
 * @param {array} - props.regions - array of landingPageDataSlide components
 * @param {string} - props.backgroundVideoURL - url of background video
 * @param {string} - props.backgroundVideoMobileURL - url of background video for mobile
 * @returns {React.ReactElement} - LandingPageHero component.regions = Array(1) [{…}]
 */
export const LandingPageDataSlider = ({backgroundVideoURL, backgroundVideoMobileURL, regions}) => {
    const slides = regions?.[0]?.components.map((component) => ({
        overlayAlignmentX: component.data?.overlayAlignmentX,
        video: component.data?.videoURL,
        videoMobile: component.data?.videoURLMobile,
        image: component.data?.imageURL,
        imageMobile: component.data?.imageURLMobile,
        richText: component.regions[0]?.components[0].data.richText
    }))

    const scrollContainerRef = useRef(null)
    const bgVideoRef = useRef(null)
    const videoRef_1 = useRef(null)
    const videoRef_2 = useRef(null)
    const videoRef_3 = useRef(null)
    const videoRef_4 = useRef(null)
    const videoRefs = [videoRef_1, videoRef_2, videoRef_3, videoRef_4]

    const [isVideo1Played, setIsVideo1Played] = useState(false)
    const [isVideo2Played, setIsVideo2Played] = useState(false)
    const [isVideo3Played, setIsVideo3Played] = useState(false)
    const [isVideo4Played, setIsVideo4Played] = useState(false)

    const isVideoPlayed = [isVideo1Played, isVideo2Played, isVideo3Played, isVideo4Played]
    const [visibleSlide, setVisibleSlide] = useState(0)
    const theme = useTheme()
    const mobileMediaQuery = `(max-width: ${theme.breakpoints.lg})`
    const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.xl})`)

    const amountSlides = 4
    const shrinkedHeaderHeight = isDesktop ? 59 : 50
    const [dataSlidesContainerHeight, setDataSlidesContainerHeight] = useState(0)

    // vertical scroll progress starting when the container enters the viewport (while scrolling down) and stops when it
    // LOWER EDGE reaches the navigation (--> the container was completely scrolled and it left the viewport)
    const {scrollYProgress: scrollYProgressScrollContainer} = useScroll({
        target: scrollContainerRef,
        offset: ['start end', `end ${shrinkedHeaderHeight}px`]
    })

    // dynamically set the height for the empty "scroll-container" which simulates scrolling the fixed-positioned slides
    const calculateContainerHeight = () => {
        const calculatedDataSlidesContainerHeight =
            (window.innerHeight - shrinkedHeaderHeight) * amountSlides
        setDataSlidesContainerHeight(calculatedDataSlidesContainerHeight)
    }

    useEffect(() => {
        window.onresize = () => {
            calculateContainerHeight()
        }
        calculateContainerHeight()
    }, [])

    useMotionValueEvent(scrollYProgressScrollContainer, 'change', (latest) => {
        if (latest > 0.666) {
            setVisibleSlide(4)
        } else if (latest > 0.4) {
            setVisibleSlide(3)
        } else if (latest > 0.25) {
            setVisibleSlide(2)
        } else if (latest > 0.125) {
            setVisibleSlide(1)
        }
    })

    const handleVideoPlayback = (videoRefIndex) => {
        //  start playing the video which is currently in the viewport and stop the previous and next video
        if (videoRefs[videoRefIndex].current) {
            videoRefs[videoRefIndex].currentTime = 0
            videoRefs[videoRefIndex].current.play()
        }
        if (videoRefIndex !== 3) {
            // stop video-playback of the previous slide (in case of scrolling upwards)
            if (videoRefs[videoRefIndex + 1].current) {
                videoRefs[videoRefIndex + 1].current.pause()
            }
        }
        if (videoRefIndex !== 0) {
            // stop video-playback of the previous slide (if scrolling downwards)
            if (videoRefs[videoRefIndex - 1].current) {
                videoRefs[videoRefIndex - 1].current.pause()
            }
        }
    }

    useEffect(() => {
        if (visibleSlide !== 0) {
            handleVideoPlayback(visibleSlide - 1)
        }
    }, [visibleSlide])

    // vertical position of fixed stage which contains the data-slides (depending on how far the empty scroll container was scrolled)
    const stageInViewYPos = useTransform(
        scrollYProgressScrollContainer,
        [0, 0.2, 0.8, 1],
        ['-94vh', '0vh', '0vh', '94vh']
    )

    const playBgVideo = () => {
        if (bgVideoRef.current) {
            bgVideoRef.current.play()
        }
    }

    const videoPlayedSetter = [
        setIsVideo1Played,
        setIsVideo2Played,
        setIsVideo3Played,
        setIsVideo4Played
    ]
    const handleVideoEnd = (index) => {
        videoPlayedSetter[index](true)
        playBgVideo()
    }
    const slidesTransition = {duration: 0.5}
    const styles = useMultiStyleConfig('LandingPageDataSlider', {
        stageInViewYPos,
        shrinkedHeaderHeight
    })
    return (
        <Box className={'pdcOuterBox pdcFullWidth'} bgColor={'black'} mt={'-5rem'}>
            <motion.div
                ref={scrollContainerRef}
                className={'emptyContainerWithHeight'}
                style={{
                    width: '100%',
                    height: dataSlidesContainerHeight
                }}
            />
            <motion.div className={'slidesStage'} style={styles.slidesStage}>
                {regions?.[0]?.components.map((component, index) => (
                    <DataSlide
                        key={`dataSlide_${index}`}
                        slideNr={index + 1}
                        slideData={slides[index]}
                        slidesTransition={slidesTransition}
                        visibleSlide={visibleSlide}
                        styles={styles}
                        videoRef={videoRefs[index]}
                        isVideoPlayed={isVideoPlayed[index]}
                        onVideoEndHandler={() => handleVideoEnd(index)}
                        mobileMediaQuery={mobileMediaQuery}
                    />
                ))}
                <motion.div>
                    <video
                        ref={bgVideoRef}
                        autoPlay={true}
                        loop={true}
                        controls={false}
                        muted
                        playsInline={true}
                        style={styles.videoPlayer}
                    >
                        <source
                            src={backgroundVideoMobileURL}
                            type="video/mp4"
                            media={mobileMediaQuery}
                        />
                        <source src={backgroundVideoURL} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </motion.div>
            </motion.div>
        </Box>
    )
}

LandingPageDataSlider.propTypes = {
    backgroundVideoURL: PropTypes.string,
    backgroundVideoMobileURL: PropTypes.string,
    regions: PropTypes.array
}

export default LandingPageDataSlider
